/* Creating a page (PRD A3): the same create_page RPC the live wizard uses (beta-code gated,
   max pages per owner), after signing the organizer up or in. */
import { supabase } from "./supabase";
import { APP, validateSlug } from "@/core";
import type { PageConfig } from "@/types/legacy";
import { addLead } from "./leads";
import { touchForServer } from "./touch";

export async function checkSlug(slug: string): Promise<{ available: boolean; reason?: string; unchecked?: boolean }> {
  const v = validateSlug(slug); if (!v.ok) return { available: false, reason: v.error };
  try { const r = await supabase().rpc("slug_available", { p_slug: slug }); if (r.error) throw new Error(r.error.message); return { available: !!r.data }; }
  catch { return { available: true, unchecked: true }; }
}

export class ConfirmEmailError extends Error { code = "confirm-email" as const; constructor() { super("confirm-email"); } }

/* Sign up (or in, when the email already has an account) and create the page. Throws
   ConfirmEmailError when Supabase wants the email confirmed before a session exists. */
export async function createPage(opts: { slug: string; email: string; password: string; template: Partial<PageConfig>; betaCode: string; ref?: string }): Promise<{ slug: string; created: true }> {
  const slug = String(opts.slug || "").toLowerCase();
  const v = validateSlug(slug); if (!v.ok) throw new Error(v.error);
  const sb = supabase();
  const session = (await sb.auth.getSession()).data.session;
  if (!session) {
    const r = await sb.auth.signUp({ email: opts.email, password: opts.password });
    if (r.error) {
      if (/already (been )?registered/i.test(r.error.message)) {
        const r2 = await sb.auth.signInWithPassword({ email: opts.email, password: opts.password });
        if (r2.error) throw new Error("That email already has an account, but the password didn't match it. Use the same password you signed up with.");
      } else throw new Error(r.error.message);
    } else if (!r.data.session) throw new ConfirmEmailError();   // confirmations are on
  }
  const r = await sb.rpc("create_page", { p_slug: slug, p_data: { ...opts.template }, p_code: String(opts.betaCode || ""), p_ref: String(opts.ref || ""), p_touch: touchForServer() });   // a referral (?ref=<slug>) is the invitation; the touch pair = attribution (0144)
  if (r.error) throw new Error(r.error.message);
  return { slug, created: true };
}

/* A keepsakes-for-later page (2026-10-09): same sign-up, then create_openwhen_page — a paid gift code stands in for the invite
   code and makes the page lifetime (tier openwhen); otherwise the beta gate applies. Never expires on its own. */
export async function createLaterRecipientPage(opts: { slug: string; email: string; password: string; template: Partial<PageConfig>; code: string; ref?: string }): Promise<{ slug: string; created: true; gift?: boolean }> {
  const slug = String(opts.slug || "").toLowerCase();
  const v = validateSlug(slug); if (!v.ok) throw new Error(v.error);
  const sb = supabase();
  const session = (await sb.auth.getSession()).data.session;
  if (!session) {
    const r = await sb.auth.signUp({ email: opts.email, password: opts.password });
    if (r.error) {
      if (/already (been )?registered/i.test(r.error.message)) {
        const r2 = await sb.auth.signInWithPassword({ email: opts.email, password: opts.password });
        if (r2.error) throw new Error("That email already has an account, but the password didn't match it. Use the same password you signed up with.");
      } else throw new Error(r.error.message);
    } else if (!r.data.session) throw new ConfirmEmailError();
  }
  const r = await sb.rpc("create_openwhen_page", { p_slug: slug, p_data: { ...opts.template }, p_code: String(opts.code || ""), p_ref: String(opts.ref || ""), p_touch: touchForServer() });   // a stored referral stands in for the invite code, as on the celebration wizard; the touch pair = attribution (0144)
  if (r.error) throw new Error(r.error.message);
  return { slug, created: true, gift: !!(r.data as { gift?: boolean } | null)?.gift };
}

/* The beta-invite request form: a lead in the database (0144, `lead_add`, emails the owner at once); the Netlify form named
   "beta" (declared in index.html) is the fallback when the database cannot be reached. See src/data/leads.ts. */
export async function requestBeta(f: { name: string; email: string; occasion: string; note: string }): Promise<void> {
  await addLead("invite_request", f);
}

/* Slug suggestions when the one they wanted is taken (2026-10-10): a few natural variants in the order people reach for
   them — with the number ("robert-80"), the full name ("robert-singer"), the year, the occasion — validated, deduped,
   never the taken one. Pure; `availableSlugs` then keeps only the free ones. */
export function slugCandidates(opts: { taken: string; honoree?: string; age?: string | number; occasion?: string; year?: number }): string[] {
  const base = String(opts.taken || "").toLowerCase().trim(); if (!base) return [];
  const slug = (s: string) => String(APP.slugify(s) || "").toLowerCase();
  const words = String(opts.honoree || "").trim().split(/\s+/).filter(Boolean);
  const first = slug(words[0] || base); const full = slug(words.join(" "));
  const age = String(opts.age || "").replace(/[^0-9]/g, "");
  const year = opts.year || new Date().getFullYear();
  const occ = String(opts.occasion || "").toLowerCase().replace(/[^a-z]/g, "");
  const raw = [
    age ? first + "-" + age : "", full && full !== base ? full : "", first + "-" + year, occ ? first + "-" + occ : "",
    age && full && full !== base ? full + "-" + age : "", first + "-" + (year % 100), age ? first + age : "", base + "-page",
  ];
  const out: string[] = [];
  for (const c of raw) { if (!c || c === base || out.includes(c)) continue; if (!validateSlug(c).ok) continue; out.push(c); }
  return out;
}
export async function availableSlugs(cands: string[], max = 3): Promise<string[]> {
  const out: string[] = [];
  for (const c of cands) { if (out.length >= max) break; try { const r = await checkSlug(c); if (r.available && !r.unchecked) out.push(c); } catch { /* skip */ } }
  return out;
}
